// Variant tag helper — assigns two-level tags to saved configurations:
//
//   • SELECTION VARIANT (length-based): "EWS-1", "EWS-2", "IU-1", "IU-2", ...
//     Numbered per prefix family per unique (model + length) in order of
//     appearance.
//   • CONFIGURATION VARIANT (selection + alphabet suffix): "EWS-1A", "EWS-1B",
//     "IU-2A", ... Suffix is appended ONLY when ≥ 2 configurations share the
//     same selection. A single-config selection keeps the base tag (e.g.
//     "EWS-1") to avoid noise.

import { tagPrefixForModel } from "./unitSchedule";
import { specForUnitModel } from "../data/orderingCodes";

// Must match ConfiguratorTab.groupKey exactly.
// Key format (4 parts): unit_model__specId__lengthFt__circuits
// unit_model is the primary discriminator — distinguishes Model C from Model C1
// even though both share spec id "C".
//
// History:
//   v20.7-: "specId__length"            (2 parts)
//   v20.8:  "specId__length__circuits"  (3 parts)
//   v20.9+: "unitModel__specId__length__circuits" (4 parts — current)
const keyForGroup = (g) => {
  const specId = specForUnitModel(g.unit_model)?.id || "";
  return `${g.unit_model}__${specId}__${g.length_ft ?? ""}__${g.circuits ?? ""}`;
};

// Old key formats for saved-project backward-compat (dual-keyed into groupByKey).
const legacyKeyForGroup = (g) => {
  const specId = specForUnitModel(g.unit_model)?.id || g.unit_model;
  return `${specId}__${g.length_ft ?? ""}__${g.circuits ?? ""}`;
};
const v207KeyForGroup = (g) => {
  const specId = specForUnitModel(g.unit_model)?.id || g.unit_model;
  return `${specId}__${g.length_ft ?? ""}`;
};

export function withVariantTags(configurations, groups) {
  if (!Array.isArray(configurations)) return [];
  const groupByKey = new Map();
  // Populate with all three key formats so old saved cfg.key strings still
  // resolve to a group. New-format key is set last so it wins for any group
  // that would have an ambiguous legacy key (e.g. Model C and Model C1
  // sharing "C__4__1" in v20.8). Both new-format keys are distinct.
  (groups || []).forEach((g) => {
    groupByKey.set(v207KeyForGroup(g), g);
    groupByKey.set(legacyKeyForGroup(g), g);
    groupByKey.set(keyForGroup(g), g);   // new 4-part key wins last
  });

  // Pass 1: assign a Selection number per (prefix, key) in first-appearance order.
  const selectionNumByPK = new Map();   // "PREFIX|KEY" → N
  const prefixCounters = {};
  configurations.forEach((v) => {
    const g = groupByKey.get(v.key);
    const prefix = g ? tagPrefixForModel(g.unit_model) : "CFG";
    const pk = `${prefix}|${v.key}`;
    if (!selectionNumByPK.has(pk)) {
      prefixCounters[prefix] = (prefixCounters[prefix] || 0) + 1;
      selectionNumByPK.set(pk, prefixCounters[prefix]);
    }
  });

  // Pass 2: total configurations per Selection group.
  const countPerSelection = new Map();
  configurations.forEach((v) => {
    const g = groupByKey.get(v.key);
    const prefix = g ? tagPrefixForModel(g.unit_model) : "CFG";
    const pk = `${prefix}|${v.key}`;
    countPerSelection.set(pk, (countPerSelection.get(pk) || 0) + 1);
  });

  // Pass 3: assign final tag (with letter suffix when ≥ 2 configs in the group).
  const seenPerSelection = new Map(); // "PREFIX|KEY" → runningIdx
  return configurations.map((v) => {
    const g = groupByKey.get(v.key);
    const prefix = g ? tagPrefixForModel(g.unit_model) : "CFG";
    const pk = `${prefix}|${v.key}`;
    const selNum = selectionNumByPK.get(pk);
    const total = countPerSelection.get(pk);
    const idx = (seenPerSelection.get(pk) || 0) + 1;
    seenPerSelection.set(pk, idx);
    const selectionTag = `${prefix}-${selNum}`;
    const tag =
      total >= 2 ? `${selectionTag}${String.fromCharCode(64 + idx)}` : selectionTag;
    return { ...v, tag, selectionTag };
  });
}

// Helper: match assignment to a room using room_id (schema v3) or room_idx (fallback).
function matchesRoomVT(a, r) {
  if (r.room_id != null && a.room_id != null) return a.room_id === r.room_id;
  return a.room_idx === r.room_idx; // backward-compat
}

// All known key shapes for a group — used to match v.key from any save era.
function allKeysForGroup(g) {
  return new Set([keyForGroup(g), legacyKeyForGroup(g), v207KeyForGroup(g)]);
}

// Check whether every room in the schedule has been fully assigned. A room
// is "assigned" when the sum of its qtys across all variants in its group
// equals the room's required qty. Multiple units in one room can map to
// different variants — that's perfectly valid.
export function isFullyAssigned(configurations, groups) {
  if (!groups || groups.length === 0) return false;
  const tagged = withVariantTags(configurations || [], groups);
  for (const g of groups) {
    const keys = allKeysForGroup(g);
    const variantsHere = tagged.filter((v) => keys.has(v.key));
    for (const r of g.rooms) {
      const sum = variantsHere.reduce((s, v) => {
        const a = (v.assignments || []).find((x) => matchesRoomVT(x, r));
        return s + (parseInt(a?.qty, 10) || 0);
      }, 0);
      if (sum !== r.qty) return false;
    }
  }
  return true;
}

// Find the variant tag that a given room belongs to, by room_idx (for
// backward-compat callers) or by searching with stable room_id.
// A room may have multiple units; assignments[] tracks per-room qty per variant.
// For the simple per-room dropdown UI we treat a room as belonging to a SINGLE
// variant when ALL of its qty is assigned to one variant.
//
// Returns: { tag, variant_id } or null when unassigned/split.
export function variantForRoom(taggedVariants, roomIdx, group) {
  if (!group) return null;
  const keys = allKeysForGroup(group);
  const candidates = taggedVariants.filter((v) => keys.has(v.key));
  // Find variant where assignments cover this room with qty === group_room.qty.
  // Look up by room_idx (positional) — callers still pass roomIdx for compat.
  const groupRoom = group.rooms.find((r) => r.room_idx === roomIdx);
  if (!groupRoom) return null;
  for (const v of candidates) {
    // Use the stable matcher so saved payloads with room_id work correctly.
    const a = (v.assignments || []).find((x) => matchesRoomVT(x, groupRoom));
    if (a && a.qty === groupRoom.qty) {
      return { tag: v.tag, variant_id: v.id };
    }
  }
  // Partial / split assignments — surface null so UI can warn
  return null;
}
